// Currency formatting + home-currency detection + cached exchange rates.
//
// "Home currency" = what the user wants conversion *back to* (their wallet).
// Detected from device locale on first load; user can override later.
//
// Exchange rates are cached to localStorage so the converter still works
// briefly offline / on slow networks (Tip tab feature).

import { useEffect, useState } from "react";
import api from "./api";

const HOME_LS_KEY = "tiplingo_home_currency_v1";
const RATE_LS_PREFIX = "tiplingo_rate_v1::"; // suffix: "USD->JPY"
const RATE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours fresh

// JPY/KRW/VND/IDR/HUF use whole numbers (no minor units).
const ZERO_DECIMAL_CURRENCIES = new Set([
  "JPY", "KRW", "VND", "IDR", "HUF", "CLP", "PYG", "RWF", "UGX", "XPF", "XAF", "XOF",
]);

const LOCALE_TO_CURRENCY = {
  US: "USD", CA: "CAD", MX: "MXN", GB: "GBP", IE: "EUR", FR: "EUR", DE: "EUR",
  IT: "EUR", ES: "EUR", PT: "EUR", NL: "EUR", BE: "EUR", AT: "EUR", FI: "EUR",
  GR: "EUR", LU: "EUR", SK: "EUR", SI: "EUR", EE: "EUR", LV: "EUR", LT: "EUR",
  HR: "EUR", CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", IS: "ISK", PL: "PLN",
  CZ: "CZK", HU: "HUF", RO: "RON", BG: "BGN", UA: "UAH", TR: "TRY", RU: "RUB",
  JP: "JPY", KR: "KRW", CN: "CNY", HK: "HKD", TW: "TWD", SG: "SGD", TH: "THB",
  VN: "VND", ID: "IDR", PH: "PHP", MY: "MYR", IN: "INR", PK: "PKR", BD: "BDT",
  LK: "LKR", NP: "NPR", KH: "KHR", LA: "LAK", MM: "MMK", AU: "AUD", NZ: "NZD",
  FJ: "FJD", AE: "AED", SA: "SAR", QA: "QAR", KW: "KWD", BH: "BHD", OM: "OMR",
  JO: "JOD", LB: "LBP", IL: "ILS", EG: "EGP", MA: "MAD", ZA: "ZAR", NG: "NGN",
  KE: "KES", ET: "ETB", BR: "BRL", AR: "ARS", CL: "CLP", CO: "COP", PE: "PEN",
  EC: "USD", UY: "UYU", VE: "VES", CR: "CRC", PA: "PAB", GT: "GTQ",
  PR: "USD", BS: "BSD", JM: "JMD", DO: "DOP", BB: "BBD", KY: "KYD", TT: "TTD",
  AW: "AWG",
};

export function detectHomeCurrency() {
  try {
    const langs = (navigator.languages || [navigator.language || ""]).filter(Boolean);
    for (const l of langs) {
      const m = l.match(/-([A-Z]{2})/i);
      if (m) {
        const cc = m[1].toUpperCase();
        if (LOCALE_TO_CURRENCY[cc]) return LOCALE_TO_CURRENCY[cc];
      }
    }
  } catch {
    /* navigator.languages not available */
  }
  return "USD";
}

export function getHomeCurrency() {
  try {
    const saved = localStorage.getItem(HOME_LS_KEY);
    if (saved) return saved;
  } catch {
    /* localStorage disabled */
  }
  return detectHomeCurrency();
}

export function setHomeCurrency(code) {
  try {
    localStorage.setItem(HOME_LS_KEY, code);
  } catch {
    /* non-fatal */
  }
}

export function formatMoney(amount, currencyCode, { showCode = false } = {}) {
  const n = Number.isFinite(amount) ? amount : 0;
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currencyCode);
  try {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    }).format(n);
    return showCode ? `${formatted} ${currencyCode}` : formatted;
  } catch {
    // Fallback if currency code is unknown to Intl
    const num = n.toLocaleString("en-US", {
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    });
    return `${num} ${currencyCode}`;
  }
}

function rateKey(from, to) {
  return `${RATE_LS_PREFIX}${from}->${to}`;
}

function readCachedRate(from, to) {
  try {
    const raw = localStorage.getItem(rateKey(from, to));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (typeof obj?.rate !== "number") return null;
    return obj; // { rate, fetched_at }
  } catch {
    return null;
  }
}

function writeCachedRate(from, to, rate) {
  try {
    localStorage.setItem(
      rateKey(from, to),
      JSON.stringify({ rate, fetched_at: Date.now() })
    );
  } catch {
    /* non-fatal */
  }
}

/**
 * React hook returning the live exchange rate between two ISO currencies.
 * - Returns 1 when from === to.
 * - Returns null only if both API + cache fail (display the local amount only).
 * - Cached in localStorage with a 6-hour TTL — older cache still used as fallback
 *   when offline (`stale: true`).
 */
export function useExchangeRate(from, to) {
  const [state, setState] = useState({ rate: from === to ? 1 : null, stale: false, loading: from !== to });

  useEffect(() => {
    let cancelled = false;
    if (!from || !to || from === to) {
      setState({ rate: 1, stale: false, loading: false });
      return () => {};
    }

    // 1) Seed from cache so the UI isn't blank while we fetch.
    const cached = readCachedRate(from, to);
    if (cached) {
      const fresh = Date.now() - cached.fetched_at < RATE_TTL_MS;
      setState({ rate: cached.rate, stale: !fresh, loading: !fresh });
      if (fresh) return () => {};
    } else {
      setState({ rate: null, stale: false, loading: true });
    }

    // 2) Hit the backend for a live rate.
    api
      .get("/exchange", { params: { from_currency: from, to_currency: to, amount: 1 } })
      .then((r) => {
        if (cancelled) return;
        const rate = Number(r.data?.rate);
        if (Number.isFinite(rate) && rate > 0) {
          writeCachedRate(from, to, rate);
          setState({ rate, stale: false, loading: false });
        } else {
          setState((s) => ({ ...s, loading: false }));
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Keep whatever cache we had; otherwise leave rate as null.
        setState((s) => ({ ...s, loading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [from, to]);

  return state;
}

export function convert(amount, rate) {
  if (!Number.isFinite(amount) || !Number.isFinite(rate)) return null;
  return amount * rate;
}
